import { Button, Input, NumberInput, Select } from "@mantine/core";
import {
  IconBuildingWarehouse,
  IconClipboardList,
  IconFileText,
  IconLayoutGrid,
  IconMapPin,
  IconPackage,
  IconStack,
  IconTruck,
  IconUser,
  IconX,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";

import { orpcClient } from "@/lib/orpc-client";
import { single } from "@/lib/orpc-batch";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

function AddStockRequestCreated() {
  const { t } = useTranslation();
  const [currentStage, setCurrentStage] = useState(0);
  const [error, setError] = useState({
    general: {
      isError: false,
      errorMessage: "",
    },
  });
  interface StockRequestData {
    name: string;
    remarks: string;
    deliveryPerson: string;
    warehouseStaffId: string;
  }

  const [stockRequestCreatedData, setStockRequestCreatedData] =
    useState<StockRequestData>({
      name: "",
      remarks: "",
      deliveryPerson: "",
      warehouseStaffId: "",
    });
  const [employeeData, setEmployeeData] = useState<any[]>([]);
  const [tempProductData, setTempProductData] = useState<any[]>([]);
  const [tempProductInput, setTempProductInput] = useState<string[]>([]);
  const [tempWarehouseData, setTempWarehouseData] = useState<any[]>([]);
  const [tempWarehouseInput, setTempWarehouseInput] = useState<string[]>([]);
  const [tempAreaInput, setTempAreaInput] = useState<string[]>([]);
  const [tempStorageLocationInput, setTempStorageLocationInput] = useState<
    string[]
  >([]);
  const [tempAmount, setTempAmount] = useState<(string | number)[]>([]);
  const [tempProductList, setTempProductList] = useState<any[]>([]);
  const [productError, setProductError] = useState<boolean[]>([]);
  const [storageLocationError, setStorageLocationError] = useState<boolean[]>(
    []
  );
  const navigate = useNavigate();

  const parseId = (value: string) =>
    typeof value === "string"
      ? value.match(/ID:\s*([a-zA-Z0-9-]+)/)?.[1] ?? null
      : null;

  const isProductInputValid = (value: string) => {
    const id = parseId(value);
    return !!id && tempProductData.some((p) => p?.id == id);
  };

  const isStorageLocationInputValid = (value: string) => {
    const id = parseId(value);
    return (
      !!id &&
      tempWarehouseData.some((w) =>
        (w?.storageLocation ?? []).some((l: any) => l?.id == id)
      )
    );
  };

  const validateProductInput = (index: number) => {
    setProductError((prev) => {
      const updated = [...prev];
      const value = tempProductInput[index];
      updated[index] = !!value && !isProductInputValid(value);
      return updated;
    });
  };

  const validateStorageLocationInput = (index: number) => {
    setStorageLocationError((prev) => {
      const updated = [...prev];
      const value = tempStorageLocationInput[index];
      updated[index] = !!value && !isStorageLocationInputValid(value);
      return updated;
    });
  };

  const handleSubmit = async (
    stockRequestCreatedData: StockRequestData,
    tempProductList: any[]
  ) => {
    if (tempProductList?.length <= 0) {
      setError((prev) => ({
        ...prev,
        general: {
          isError: true,
          errorMessage: t("stockRequest.productListMinOne"),
        },
      }));
      return;
    }

    const invalidProduct = tempProductList.find(
      (item) => !item?.product?.id || !item?.storageLocation || !item?.amount
    );

    if (invalidProduct) {
      setError((prev) => ({
        ...prev,
        general: {
          isError: true,
          errorMessage: t("stockRequest.ensureProductComplete"),
        },
      }));
      return;
    }

    setError((prev) => ({
      ...prev,
      general: {
        isError: false,
        errorMessage: "",
      },
    }));

    const dataToSubmit = {
      name: stockRequestCreatedData.name,
      remarks: stockRequestCreatedData.remarks || undefined,
      deliveryPerson: stockRequestCreatedData.deliveryPerson || undefined,
      warehouseStaffId: stockRequestCreatedData.warehouseStaffId || undefined,
    };

    try {
      const result = await single(
        orpcClient.stockRequest.create([dataToSubmit])
      );
      if (result) {
        const batchResults = await orpcClient.stockRequestProduct.create(
          tempProductList.map((data: any) => ({
            productId: data?.product?.id,
            storageLocationId: data?.storageLocation,
            amount: data?.amount,
            stockRequestId: result?.id,
          }))
        );
        const allSuccessful = batchResults.every((res) => res.success);
        if (allSuccessful) {
          setCurrentStage(1);
        } else {
          console.warn("Some batch operations failed:", batchResults);
        }
      }
    } catch (error) {
      console.error("apiUserPostStockRequest_error", error);
      return;
    }
  };

  const handleDataChange = (name: keyof StockRequestData, value: string) => {
    setStockRequestCreatedData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleDeleteProductList = (index: number) => {
    const updatedProductList = [...tempProductList];

    updatedProductList.splice(index, 1);

    setTempProductInput((prevInputs) => {
      const updatedInputs = [...prevInputs];
      updatedInputs.splice(index, 1);
      return updatedInputs;
    });

    setTempWarehouseInput((prevInputs) => {
      const updatedInputs = [...prevInputs];
      updatedInputs.splice(index, 1);
      return updatedInputs;
    });

    setTempAreaInput((prevInputs) => {
      const updatedInputs = [...prevInputs];
      updatedInputs.splice(index, 1);
      return updatedInputs;
    });

    setTempStorageLocationInput((prevInputs) => {
      const updatedInputs = [...prevInputs];
      updatedInputs.splice(index, 1);
      return updatedInputs;
    });

    setTempAmount((prevInputs) => {
      const updatedInputs = [...prevInputs];
      updatedInputs.splice(index, 1);
      return updatedInputs;
    });

    setProductError((prevInputs) => {
      const updatedInputs = [...prevInputs];
      updatedInputs.splice(index, 1);
      return updatedInputs;
    });

    setStorageLocationError((prevInputs) => {
      const updatedInputs = [...prevInputs];
      updatedInputs.splice(index, 1);
      return updatedInputs;
    });

    setTempProductList(updatedProductList);
  };

  const handleAddProductList = () => {
    setTempProductList((prev) => [...prev, {}]);
  };

  const handleDisplaySelectMembers = (data: any[]) => {
    return data?.map((t) => ({
      value: t?.id,
      label: t?.name,
    }));
  };
  const handleDisplaySelectWarehouse = (data: any[]) => {
    return data?.map((t) => ({ value: t?.id, label: t?.name }));
  };
  const handleDisplaySelectArea = (
    warehouseId: string,
    warehouseData: any[]
  ) => {
    if (!warehouseId) return [];

    const selectedData = warehouseData.find((e: any) => e?.id == warehouseId);

    return (selectedData?.areas ?? []).map((area: any) => ({
      value: area?.id,
      label: area?.name,
    }));
  };
  const handleDisplaySelectStorageLocation = (
    warehouseId: string,
    areaId: string,
    warehouseData: any[]
  ) => {
    if (!warehouseId || !areaId) return [];

    const selectedWarehouse = warehouseData.find(
      (e: any) => e?.id == warehouseId
    );
    const selectedArea = (selectedWarehouse?.areas ?? []).find(
      (a: any) => a?.id == areaId
    );

    return (selectedArea?.storageLocation ?? []).map((location: any) => ({
      value: location?.id,
      label: location?.name,
    }));
  };

  const handleChangeWarehouseStaff = (value: string, employeeData: any[]) => {
    const idNumber = parseId(value);
    const selectedData = employeeData.find((e: any) => e?.id == idNumber);

    handleDataChange("warehouseStaffId", selectedData?.id ?? "");
  };

  const handleOnOptionSubmit = (
    type: string,
    e: string,
    data: any[],
    index: number
  ) => {
    let selectedData: any;
    if (type === "product") {
      let match = e.match(/ID:\s*([a-zA-Z0-9-]+)/);
      let idNumber = match ? match[1] : null;
      selectedData = data.find((e: any) => e?.id == idNumber);
      setTempProductList((prev) => {
        const updatedList = [...prev];
        updatedList[index] = { ...updatedList[index], [type]: selectedData };
        return updatedList;
      });
      setProductError((prev) => {
        const updated = [...prev];
        updated[index] = false;
        return updated;
      });
    } else {
      let idBehindMatch = e.match(/ID:\s*([a-zA-Z0-9-]+)/);
      let documentID = idBehindMatch ? idBehindMatch[1].trim() : null;
      setTempProductList((prev) => {
        const updatedList = [...prev];
        updatedList[index] = {
          ...updatedList[index],
          storageLocation: documentID,
        };
        return updatedList;
      });
      setStorageLocationError((prev) => {
        const updated = [...prev];
        updated[index] = false;
        return updated;
      });
    }
  };

  const { name, remarks, deliveryPerson } = stockRequestCreatedData;

  useEffect(() => {
    const fetchData = async () => {
      try {
        let result = await orpcClient.employee.list({});
        setEmployeeData(result);
      } catch (error) {
        console.error("Error fetching employee data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let result = await orpcClient.product.list({});

        setTempProductData(result);
      } catch (error) {
        console.error("Error fetching products data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let result = await orpcClient.warehouse.list({});

        const warehousesWithLocations = await Promise.all(
          (result ?? []).map(async (warehouse: any) => {
            const areas = await orpcClient.warehouseArea.list({
              filter: { warehouseId: warehouse?.id },
            });
            const storageLocation = (areas ?? []).flatMap(
              (area: any) => area?.storageLocation ?? []
            );
            return { ...warehouse, areas: areas ?? [], storageLocation };
          })
        );

        setTempWarehouseData(warehousesWithLocations);
      } catch (error) {
        console.error("Error fetching warehouses data:", error);
      }
    };

    fetchData();
  }, []);

  const fieldWrapper = (
    label: React.ReactNode,
    Icon: React.ComponentType<{ size?: number | string }> | undefined,
    children: React.ReactNode
  ) => (
    <div className="space-y-1.5">
      <label className="flex items-center gap-1.5 text-xs text-gray-500">
        {Icon ? <Icon size={13} /> : null}
        {label}
      </label>
      {children}
    </div>
  );

  return currentStage === 0 ? (
    <div className="min-h-screen p-8">
      <div className="mx-auto max-w-3xl rounded-xl bg-white p-8 space-y-5">
        <div>
          <div className="text-4xl font-bold" style={{ color: "#6F6F6F" }}>
            {t("stockRequest.createStockRequest") || "建立領料單"}
          </div>
          <div className="mt-2 text-sm text-gray-500">
            {t("stockRequest.stockRequestManagement") || "領料單管理"}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconFileText size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("stockRequest.basicInfo") || "基本資訊"}
            </h3>
          </div>
          <div className="p-6">
            {fieldWrapper(
              t("stockRequest.stockRequestName") || "領料單名稱",
              IconClipboardList,
              <Input
                value={name}
                onChange={({ target: { value } }) =>
                  handleDataChange("name", value)
                }
                leftSection={<IconClipboardList size={16} />}
              />
            )}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconPackage size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("stockRequest.productList") || "商品清單"}
            </h3>
          </div>
          <div className="space-y-4 p-6">
            {tempProductList.map((_item, i) => {
              return (
                <div
                  className="rounded-lg border border-gray-200 p-4"
                  key={i}
                >
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-500">
                      #{i + 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleDeleteProductList(i)}
                      className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-red-500 hover:bg-red-50"
                    >
                      <IconX size={16} />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {fieldWrapper(
                      t("stockRequest.productName") || "商品名稱",
                      IconPackage,
                      <Select
                        searchable
                        nothingFoundMessage="查無資料"
                        leftSection={<IconPackage size={16} />}
                        data={handleDisplaySelectMembers(tempProductData)}
                        value={tempProductInput[i] || null}
                        onChange={(value) => {
                          setTempProductInput((prev) => {
                            const updated = [...prev];
                            updated[i] = value;
                            return updated;
                          });
                          const selectedData = tempProductData.find(
                            (p) => p?.id == value
                          );
                          setTempProductList((prev) => {
                            const updated = [...prev];
                            updated[i] = {
                              ...updated[i],
                              product: selectedData,
                            };
                            return updated;
                          });
                        }}
                      />
                    )}

                    {fieldWrapper(
                      t("stockRequest.warehouseLabel") || "倉庫",
                      IconBuildingWarehouse,
                      <Select
                        searchable
                        nothingFoundMessage="查無資料"
                        leftSection={<IconBuildingWarehouse size={16} />}
                        data={handleDisplaySelectWarehouse(tempWarehouseData)}
                        value={tempWarehouseInput[i] || null}
                        onChange={(value) => {
                          setTempWarehouseInput((prev) => {
                            const updated = [...prev];
                            updated[i] = value;
                            return updated;
                          });

                          setTempAreaInput((prev) => {
                            const updated = [...prev];
                            updated[i] = null;
                            return updated;
                          });
                          setTempStorageLocationInput((prev) => {
                            const updated = [...prev];
                            updated[i] = null;
                            return updated;
                          });
                          setTempProductList((prev) => {
                            const updated = [...prev];
                            updated[i] = {
                              ...updated[i],
                              storageLocation: null,
                            };
                            return updated;
                          });
                        }}
                      />
                    )}

                    {fieldWrapper(
                      t("stockRequest.areaLabel") || "區",
                      IconLayoutGrid,
                      <Select
                        searchable
                        nothingFoundMessage="查無資料"
                        leftSection={<IconLayoutGrid size={16} />}
                        data={handleDisplaySelectArea(
                          tempWarehouseInput[i],
                          tempWarehouseData
                        )}
                        value={tempAreaInput[i] || null}
                        onChange={(value) => {
                          setTempAreaInput((prev) => {
                            const updated = [...prev];
                            updated[i] = value;
                            return updated;
                          });

                          setTempStorageLocationInput((prev) => {
                            const updated = [...prev];
                            updated[i] = null;
                            return updated;
                          });
                          setTempProductList((prev) => {
                            const updated = [...prev];
                            updated[i] = {
                              ...updated[i],
                              storageLocation: null,
                            };
                            return updated;
                          });
                        }}
                      />
                    )}

                    {fieldWrapper(
                      t("stockRequest.storageLocationLabel") || "儲位",
                      IconMapPin,
                      <Select
                        searchable
                        nothingFoundMessage="查無資料"
                        leftSection={<IconMapPin size={16} />}
                        data={handleDisplaySelectStorageLocation(
                          tempWarehouseInput[i],
                          tempAreaInput[i],
                          tempWarehouseData
                        )}
                        value={tempStorageLocationInput[i] || null}
                        onChange={(value) => {
                          setTempStorageLocationInput((prev) => {
                            const updated = [...prev];
                            updated[i] = value;
                            return updated;
                          });
                          setTempProductList((prev) => {
                            const updated = [...prev];
                            updated[i] = {
                              ...updated[i],
                              storageLocation: value,
                            };
                            return updated;
                          });
                        }}
                      />
                    )}

                    {fieldWrapper(
                      t("stockRequest.quantity") || "數量",
                      IconStack,
                      <NumberInput
                        leftSection={<IconStack size={16} />}
                        value={tempAmount[i] || ""}
                        onChange={(e) => {
                          setTempProductList((prev) => {
                            const updatedList = [...prev];
                            updatedList[i] = {
                              ...updatedList[i],
                              amount: e,
                            };
                            return updatedList;
                          });

                          setTempAmount((prev) => {
                            const updated = [...prev];
                            updated[i] = e;
                            return updated;
                          });
                        }}
                      />
                    )}
                  </div>
                </div>
              );
            })}

            <Button
              variant="default"
              fullWidth
              radius="md"
              onClick={() => handleAddProductList()}
            >
              {t("stockRequest.addProductRow") || "增加商品列"}
            </Button>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconTruck size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("stockRequest.deliveryInfo") || "交貨資訊"}
            </h3>
          </div>
          <div className="space-y-4 p-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {fieldWrapper(
                t("stockRequest.requestPerson") || "領料人員",
                IconUser,
                <Input
                  leftSection={<IconUser size={16} />}
                  value={deliveryPerson}
                  onChange={(e) =>
                    handleDataChange("deliveryPerson", e.target.value)
                  }
                />
              )}
              {fieldWrapper(
                t("stockRequest.outboundStaff") || "出庫人員",
                IconTruck,
                <Select
                  searchable
                  clearable
                  nothingFoundMessage="查無資料"
                  leftSection={<IconTruck size={16} />}
                  data={employeeData
                    ?.filter((t: any) => t?.status !== "resigned")
                    .map((t: any) => ({ value: t?.id, label: t?.name }))}
                  value={stockRequestCreatedData.warehouseStaffId || null}
                  onChange={(value) =>
                    handleDataChange("warehouseStaffId", value ?? "")
                  }
                />
              )}
            </div>
            {fieldWrapper(
              t("stockRequest.remarks") || "備註",
              IconFileText,
              <Input
                leftSection={<IconFileText size={16} />}
                value={remarks}
                onChange={(e) => handleDataChange("remarks", e.target.value)}
              />
            )}
          </div>
        </div>

        <div className="flex gap-4">
          <Button
            variant="default"
            fullWidth
            radius="md"
            onClick={() => navigate(`/hrMainPage/stockRequestCreated`)}
          >
            {t("stockRequest.cancel") || "取消"}
          </Button>

          <Button
            variant="filled"
            color="gray"
            fullWidth
            radius="md"
            onClick={() =>
              handleSubmit(stockRequestCreatedData, tempProductList)
            }
          >
            {t("stockRequest.create") || "建立"}
          </Button>
        </div>
        {error.general.isError && (
          <Input.Error>{error.general.errorMessage}</Input.Error>
        )}
      </div>
    </div>
  ) : (
    <div className="min-h-screen flex h-full items-center justify-center p-14 text-center">
      <div className="w-[487px] rounded-xl bg-white p-8">
        <div style={{ color: "#6F6F6F" }}>
          {t("stockRequest.stockRequestCreatedSuccess") || "建立領料單成功！"}
        </div>
        <Button
          variant="filled"
          color="gray"
          style={{ width: "100%", margin: "1rem 0" }}
          radius="md"
          onClick={() => navigate("/hrMainPage/stockRequestCreated")}
        >
          {t("stockRequest.confirm") || "確定"}
        </Button>
      </div>
    </div>
  );
}
export default AddStockRequestCreated;
